/**
 * Asset Registry API
 * All calls require callerEmail from UserContext.
 */

import { apiFetch } from '../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * Fetch sites available to the caller.
 * GET /get_sites?caller_email={email}
 */
export async function getRegistrySites(callerEmail) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/get_sites?${params}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

/**
 * Fetch all tools for a site.
 * GET /get_tools?site_id={siteId}&caller_email={email}
 * Then bulk-fetch part numbers and asset tags by site_id in parallel
 * and merge them onto each tool for client-side search.
 */
export async function getRegistryTools(siteId, callerEmail) {
  const params = new URLSearchParams({ site_id: siteId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const toolsRes = await apiFetch(`${API_BASE_URL}/get_tools?${params}`);
  if (!toolsRes.ok) throw new Error(`get_tools error: ${toolsRes.status}`);
  return toolsRes.json();
}

/**
 * Canonical multi-container search (Tools + AssetTags + PartNumbers) for a
 * site. Mirrors inventoryApi.searchInventoryTools (the S4/S5 reference impl).
 * Returns raw snake_case tool docs; tools matched via asset tag / part
 * number carry a `_match_context` annotation. Used when the query meets the
 * minimum length; shorter queries fall back to getRegistryTools.
 * GET /search_inventory?site_id={siteId}&q={query}&caller_email={email}
 */
export async function searchRegistryTools(siteId, query, callerEmail) {
  const params = new URLSearchParams({ site_id: siteId, q: query });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/search_inventory?${params}`);
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch part numbers for a single tool (detail panel on-demand).
 * GET /manage_part_number?tool_id={toolId}&caller_email={email}
 */
export async function getToolPartNumbers(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?${params}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

/**
 * Fetch asset tags for a single tool (detail panel on-demand).
 * GET /manage_asset_tag?tool_id={toolId}&caller_email={email}
 */
export async function getToolAssetTags(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?${params}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

/**
 * Fetch lifecycle profile for a single tool.
 * GET /manage_lifecycle_profile?tool_id={toolId}&caller_email={email}
 */
export async function getToolLifecycleProfile(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_lifecycle_profile?${params}`);
  if (res.status === 404) return null;
  if (!res.ok) return null;
  return res.json();
}

/**
 * Fetch assessment history for a tool (max 10, most recent first).
 * GET /manage_lifecycle_profile?action=assessments&tool_id={toolId}&caller_email={email}
 */
export async function getToolLifecycleAssessments(toolId, callerEmail) {
  const params = new URLSearchParams({ action: 'assessments', tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_lifecycle_profile?${params}`);
  if (!res.ok) return [];
  return res.json();
}

/**
 * Fetch disposal workflow for a tool.
 * GET /get_disposal?tool_id={toolId}&caller_email={email}
 * Returns array — we take the first record.
 */
export async function getToolDisposalWorkflow(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/get_disposal?${params}`);
  if (!res.ok) return null;
  const data = await res.json();
  return Array.isArray(data) && data.length > 0 ? data[0] : null;
}

/**
 * Fetch media/photos for a tool.
 * GET /get_media?tool_id={toolId}
 * Returns array of media objects with blob_url (SAS), file_name, photo_type, etc.
 */
export async function getToolMedia(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/get_media?${params}`);
  if (!res.ok) return [];
  return res.json();
}
